import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { FaUser } from 'react-icons/fa';
import apiAxios from 'helpers/apiAxios';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38, height: 38, borderRadius: 8,
    borderColor: state.isFocused ? '#4f46e5' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
    '&:hover': { borderColor: '#4f46e5' },
    transition: 'all 0.15s ease',
  }),
  valueContainer: (base) => ({ ...base, height: 38, padding: '0 10px' }),
  input:          (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 38 }),
  clearIndicator: (base) => ({ ...base, color: '#94a3b8', padding: '0 6px', cursor: 'pointer', '&:hover': { color: '#ef4444' } }),
  menu:           (base) => ({ ...base, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', marginTop: 4 }),
  menuList:       (base) => ({ ...base, padding: 4, maxHeight: 220 }),
  menuPortal:     (base) => ({ ...base, zIndex: 100001 }),
  option: (base, state) => ({
    ...base, padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#f8fafc' : 'transparent',
    color: state.isSelected ? '#fff' : '#1e293b',
    '&:active': { backgroundColor: state.isSelected ? '#4338ca' : '#f1f5f9' },
  }),
  singleValue: (base) => ({ ...base, color: '#1e293b', fontSize: 14 }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: 14 }),
};

const Placeholder = (props) => (
  <components.Placeholder {...props}>
    <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-400">
      <FaUser size={12} />
      <span className="tw-text-sm">{props.children}</span>
    </div>
  </components.Placeholder>
);

Placeholder.propTypes = {
  children: PropTypes.node,
};

const ContactSelect = ({ label, value, onChange, projectId, placeholder = 'Search contact...' }) => {
  const loadOptions = useCallback(async (inputValue) => {
    try {
      const res = await apiAxios.get(`/crm/projects/${projectId}/project_contacts`, {
        params: inputValue ? { q: inputValue } : {},
      });
      return (res.data?.data || []).map(c => ({ value: c.id, label: c.name }));
    } catch {
      return [];
    }
  }, [projectId]);

  const selected = value ? { value: value.id, label: value.name } : null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      {label && (
        <label className="tw-text-xs tw-font-semibold tw-text-slate-600 tw-mb-0.5 tw-block">{label}</label>
      )}
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        value={selected}
        onChange={opt => onChange(opt ? { id: opt.value, name: opt.label } : null)}
        placeholder={placeholder}
        isClearable
        styles={selectStyles}
        components={{ Placeholder }}
        menuPosition="fixed"
        menuPortalTarget={document.body}
        noOptionsMessage={({ inputValue }) => inputValue ? 'No contacts found' : 'No contacts in this project'}
      />
    </div>
  );
};

ContactSelect.propTypes = {
  label:     PropTypes.string,
  value:     PropTypes.shape({ id: PropTypes.any, name: PropTypes.string }),
  onChange:  PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  placeholder: PropTypes.string,
};

export default ContactSelect;
